import { create } from "zustand";
import { getCredits, getSubscriptions, getLoginState } from "@/api";
import { APICode, LOGIN_SCENE, PROJECT_TYPE } from "@/constants";
import { loadPaymentScript, loadLoginScript } from "@/utils";

type States = {
  credits: number | undefined;
  userInfo: any;
  isVip: boolean | undefined;
  isSvip: boolean | undefined;
}

type Getters = {
  getters: {
    isLogin: boolean | undefined;
    creditsVisible: boolean;
  };
}

type Mutations = {
  setUserInfo: (value: any) => void;
}

type Actions = {
  reset: () => void;
  fetchCredits: () => Promise<number>;
  fetchUserInfo: () => Promise<void>;
  fetchVipStatus: () => Promise<void>;
  showPayment: (options: any) => Promise<void>;
  showLogin: (options: any) => Promise<void>;
}

const initialState: States = {
  credits: undefined,
  userInfo: null,
  isVip: undefined,
  isSvip: undefined,
}

const useUserStore = create<States & Getters & Mutations & Actions>((set, get) => ({
  // states
  ...initialState,

  // getters
  getters: {
    get isLogin() {
      const { userInfo } = get();
      return userInfo && userInfo.id
    },
    get creditsVisible() {
      const { credits, getters } = get();
      return !!(getters.isLogin && typeof credits === "number");
    }
  },
  
  // mutations
  setUserInfo: (value) => set({ userInfo: value }),

  // actions
  reset: () => set(initialState),
  fetchCredits: async () => {
    const { code, data } = await getCredits();
    if (code === APICode.Success) {
      const { subScore } = data;
      set({ credits: subScore });
      return subScore;
    } 
  },
  fetchUserInfo: () => {
    return new Promise<void>((resolve, reject) => {
      getLoginState()
        .then((res: any) => {
          if (res && res.code === '000') {            
            set({ userInfo: res.data });
          } 
          resolve();
        })
        .catch(reject);
    })
  },
  fetchVipStatus() {
    return new Promise((resolve, reject) => {
      const { userInfo } = get();
      getSubscriptions().then((res: any) => {
        if (res && res.code === '000') {
          let sub = res.data || [];
          if (
            sub.find((m: any) => m.productName === 'fotor' && !m.expiration) ||
            userInfo?.superFotor
          ) {
            set({ isVip: true });
          } else {
            set({ isVip: false });
          }
          if (sub.find((m: any) => m.productName === 'pxbee' && !m.expiration)) {
            // 超级vip
            set({ isVip: true, isSvip: true });
          } else {
            set({ isSvip: false });
          }
        }
        resolve();
      }).catch(reject);
    })
	},
  showPayment({
		position,
		otherParam,
	}) {
    return new Promise((resolve, reject) => {
      const { fetchVipStatus, fetchCredits, isVip, isSvip } = get();
      loadPaymentScript()
        .then(() => {
          window['Payment'].showPayment(
            async (data: any) => {
              let { type, code } = data;
              if (code === '006' || code === '007') {
                fetchVipStatus();
                fetchCredits();
                resolve();
              } else if (type === 'success') {
                fetchVipStatus();
                fetchCredits();
                resolve();
              } else if (type === 'fail') {
                reject();
              } else {
                reject();
              }
            },
            0,
            (isVip || isSvip) ? "credit_sub" : "other",
            '',
            '',
            { position, project_type: PROJECT_TYPE },
            otherParam
          );
        })
        .catch(reject);
    })
	},
  showLogin({
		isSignIn,
		options,
		isShowScenses,
		otherParam = {},
    callback,
	}) {
		return new Promise<void>((resolve, reject) => {
      loadLoginScript()
        .then(() => {
          let signType = isSignIn ? 'login' : 'signUp';
          let position = (options && options.position) || '';
          window.login.showLogin(
            signType,
            async (res: any) => {
              let { code, userInfo } = res;
              if (code === 0 || code === 1 || code === 2) {
                set({ userInfo: userInfo.data });
                callback?.();
                resolve();
              } else {
                reject();
              }
            },
            '',
            { position, project_type: PROJECT_TYPE },
            isShowScenses,
            { 
              ...otherParam,
              scenesId: LOGIN_SCENE,
            }
          ); 
        })
        .catch(reject);
    })
	},
}))

export default useUserStore;